import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FiHome, FiUsers, FiMail, FiTag, FiStar, FiSettings, FiBookOpen } from "react-icons/fi";
import { GrAnnounce } from "react-icons/gr";
import { TiWeatherSunny } from "react-icons/ti";
import { IoCloseSharp } from "react-icons/io5";
import { FaRegMoon } from "react-icons/fa";
import { useTheme } from "../contexts/ThemeContext";
import { ROUTES } from "../routes/routes";
import Logo from "../components/ui/Logo";
import { strings } from "../strings/strings";
import { apiCall } from "../api/fetchClient";
import { GET_CONTACT_REQUESTS_URL } from "../api/url";

const Sidebar = ({ isOpen, onClose }) => {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [superAdminData, setSuperAdminData] = useState(() => JSON.parse(localStorage.getItem("superAdmin")) || null);

  useEffect(() => {
    const handleProfileUpdate = () => {
      const data = JSON.parse(localStorage.getItem("superAdmin"));
      setSuperAdminData(data);
    };

    window.addEventListener("profileUpdated", handleProfileUpdate);
    window.addEventListener("storage", handleProfileUpdate);

    return () => {
      window.removeEventListener("profileUpdated", handleProfileUpdate);
      window.removeEventListener("storage", handleProfileUpdate);
    };
  }, []);

  const [pendingContactCount, setPendingContactCount] = useState(0);

  useEffect(() => {
    const fetchPendingCount = async () => {
      try {
        const response = await apiCall(`${GET_CONTACT_REQUESTS_URL}?status=pending&limit=1`, "GET");
        if (response.success) {
          setPendingContactCount(response?.results?.pagination?.totalItems || 0);
        }
      } catch (error) {}
    };

    fetchPendingCount();

    window.addEventListener("contactRequestUpdated", fetchPendingCount);

    return () => {
      window.removeEventListener("contactRequestUpdated", fetchPendingCount);
    };
  }, []);

  const menuItems = [
    { id: "dashboard", label: "Dashboard", icon: FiHome, path: ROUTES.ADMIN_DASHBOARD },
    { id: "Company", label: "Company", icon: FiUsers, path: ROUTES.ADMIN_COMPANY },
    { id: "Announcements", label: "Announcements", icon: GrAnnounce, path: ROUTES.ADMIN_ANNOUNCEMENTS },
    { id: "ContactRequests", label: "Contact Requests", icon: FiMail, path: ROUTES.ADMIN_CONTACT_REQUESTS },
    { id: "Brands", label: "Brands", icon: FiTag, path: ROUTES.ADMIN_BRANDS },
    { id: "Reviews", label: "Reviews", icon: FiStar, path: ROUTES.ADMIN_REVIEWS },
    { id: "Blog", label: "Blog", icon: FiBookOpen, path: ROUTES.ADMIN_BLOG },
    { id: "Settings", label: "Settings", icon: FiSettings, path: ROUTES.ADMIN_SETTINGS },
  ];

  const handleMenuClick = (path) => {
    navigate(path);
    onClose();
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden" onClick={onClose} />}

      {/* Sidebar */}
      <aside
        className={`fixed lg:relative w-64 h-full bg-white dark:bg-slate-900 border-r border-gray-200 dark:border-slate-800 flex flex-col z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        {/* Logo Section */}
        <div
          onClick={() => {
            navigate(ROUTES.ADMIN_DASHBOARD);
          }}
          className="p-4 border-b border-gray-200 dark:border-slate-800 flex items-center justify-between cursor-pointer"
        >
          <Logo data={superAdminData} />
          {/* Close button for mobile */}
          <button
            onClick={onClose}
            className="lg:hidden p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          >
            <IoCloseSharp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Menu Items */}
        <nav className="flex-1 p-4 space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            return (
              <button
                key={item.id}
                onClick={() => handleMenuClick(item.path)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800"
                }`}
              >
                <div className="flex items-center gap-3">
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </div>
                {item.id === "ContactRequests" && pendingContactCount > 0 && (
                  <span className="ml-auto inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                    {pendingContactCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Theme Toggle */}
        <div className="p-4 border-t border-gray-200 dark:border-slate-800">
          <button
            onClick={toggleTheme}
            className="w-full flex items-center gap-3 px-4 py-3 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          >
            {theme === "light" ? (
              <>
                <FaRegMoon className="w-4 h-4" />
                <span className="font-medium">{strings.DARK}</span>
              </>
            ) : (
              <>
                <TiWeatherSunny className="w-5 h-5" />
                <span className="font-medium">{strings.LIGHT}</span>
              </>
            )}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
